// src/components/GraficoCampanhas.js

import React from 'react';
import PropTypes from 'prop-types';
import { 
  PieChart, 
  Pie, 
  Cell, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Label
} from 'recharts';
import { Box, Typography } from '@mui/material';

const STATUS_COLORS = {
  'Pendente': '#ffc107',
  'Em_Analise': '#2196f3',
  'Aprovada': '#4caf50',
  'Matriculado': '#3f51b5',
  'Rejeitada': '#f44336',
  'Desistente': '#9e9e9e'
};

const STATUS_LABELS = {
  'Pendente': 'Pendentes',
  'Em_Analise': 'Em Análise',
  'Aprovada': 'Aprovadas',
  'Matriculado': 'Matriculados',
  'Rejeitada': 'Rejeitadas',
  'Desistente': 'Desistentes'
};

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return percent > 0.05 ? (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  ) : null;
};

// Definindo PropTypes para o CustomTooltip
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box 
        sx={{ 
          bgcolor: 'background.paper',
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: 1
        }}
      >
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>{data.name}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Quantidade: {data.value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Percentual: {(data.percent * 100).toFixed(1)}%
        </Typography>
      </Box>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      payload: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
        percent: PropTypes.number.isRequired
      })
    })
  )
};

CustomTooltip.defaultProps = {
  active: false,
  payload: []
};

// PropTypes para renderCustomizedLabel
renderCustomizedLabel.propTypes = {
  cx: PropTypes.number.isRequired,
  cy: PropTypes.number.isRequired,
  midAngle: PropTypes.number.isRequired,
  innerRadius: PropTypes.number.isRequired,
  outerRadius: PropTypes.number.isRequired,
  percent: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired
};

const GraficoCampanhas = ({ data }) => {
  // Garantir que 'data' seja um objeto válido
  const safeData = data && typeof data === 'object' ? data : {};

  // Calcular o total apenas se safeData tiver valores
  const total = Object.values(safeData).reduce((a, b) => a + b, 0);

  // Evitar divisão por zero
  const chartData = total > 0 
    ? Object.entries(safeData).map(([status, valor]) => ({
        name: STATUS_LABELS[status] || status,
        value: valor,
        percent: valor / total
      }))
    : [];

  if (chartData.length === 0) {
    return (
      <Box 
        sx={{ 
          height: 300, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <Typography color="text.secondary">
          Nenhum dado disponível
        </Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={renderCustomizedLabel}
          outerRadius={100}
          fill="#8884d8"
          dataKey="value"
        >
          {chartData.map((entry, index) => {
            // Encontrar a chave do status com base no label
            const statusKey = Object.keys(STATUS_LABELS).find(
              key => STATUS_LABELS[key] === entry.name
            );

            // Obter a cor correspondente ou uma cor padrão
            const fillColor = STATUS_COLORS[statusKey] || '#cccccc';

            return (
              <Cell 
                key={`cell-${index}`} 
                fill={fillColor} 
              />
            );
          })}
          <Label value="Total" position="center" />
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};

GraficoCampanhas.propTypes = {
  data: PropTypes.objectOf(PropTypes.number).isRequired
};

export default GraficoCampanhas;
